import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, CheckCircle2, Film, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { AttachmentPicker } from "./AttachmentPicker";
import {
  SEVERITY_HINT, SEVERITY_STYLE, fmtDate, fmtDuration, fmtClock,
} from "../../lib/support";

export const ReportProblemForm = ({ prefill, onOpenTicket }) => {
  const [meta, setMeta] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [category, setCategory] = useState(prefill?.category || "");
  const [severity, setSeverity] = useState(prefill?.severity || "incorrect");
  const [jobId, setJobId] = useState(prefill?.job_id || "none");
  const [description, setDescription] = useState("");
  const [expected, setExpected] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, j] = await Promise.all([
        api.get("/support/meta"),
        api.get("/support/jobs"),
      ]);
      setMeta(m.data);
      setJobs(j.data.jobs || []);
    } catch {
      toast.error("Could not load the support form");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const jobRequired = useMemo(
    () => !!category
      && (meta?.job_linked_categories || []).includes(category)
      && jobs.length > 0,
    [category, meta, jobs.length]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!category) return toast.error("Pick a category");
    if (description.trim().length < 10) return toast.error("Describe the problem in a bit more detail");
    if (jobRequired && jobId === "none") return toast.error("Select the video this problem happened on");
    setBusy(true);
    try {
      const { data } = await api.post("/support/tickets", {
        category,
        severity,
        description: description.trim(),
        expected_result: expected.trim() || null,
        job_id: jobId === "none" ? null : jobId,
        cue: prefill?.cue_index ? { cue_index: prefill.cue_index } : null,
        attachment_ids: attachments.map((a) => a.attachment_id),
      });
      setCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit the report");
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <div className="border border-emerald-500/30 bg-emerald-500/5 p-6 md:p-8" data-testid="ticket-created-panel">
        <CheckCircle2 className="w-8 h-8 text-emerald-400 mb-4" />
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white">
          Your support request has been received.
        </h2>
        <p className="text-sm text-zinc-400 mt-2">
          A confirmation is on its way to your email. Keep the ticket ID handy.
        </p>
        <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
          <div>
            <dt className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Ticket ID</dt>
            <dd className="font-mono text-white mt-1" data-testid="created-ticket-id">{created.ticket_id}</dd>
          </div>
          <div>
            <dt className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Category</dt>
            <dd className="text-white mt-1" data-testid="created-ticket-category">{created.category_label}</dd>
          </div>
          <div>
            <dt className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Status</dt>
            <dd className="text-white mt-1" data-testid="created-ticket-status">{created.status_label}</dd>
          </div>
          <div>
            <dt className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Created</dt>
            <dd className="text-white mt-1" data-testid="created-ticket-date">{fmtDate(created.created_at)}</dd>
          </div>
          {created.job && (
            <div className="sm:col-span-2">
              <dt className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Related video</dt>
              <dd className="text-white mt-1 truncate" data-testid="created-ticket-video">
                {created.job.video_name}
              </dd>
            </div>
          )}
        </dl>
        <div className="mt-7 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => onOpenTicket?.(created.ticket_id)}
            data-testid="created-view-ticket-btn"
            className="text-xs font-mono uppercase tracking-widest bg-white text-zinc-950 hover:bg-zinc-200 px-5 py-2.5 transition-colors"
          >
            View ticket
          </button>
          <button
            type="button"
            onClick={() => {
              setCreated(null); setDescription(""); setExpected("");
              setAttachments([]); setJobId("none"); setCategory("");
            }}
            data-testid="created-report-another-btn"
            className="text-xs font-mono uppercase tracking-widest border border-zinc-800 hover:bg-zinc-900 text-zinc-300 px-5 py-2.5 transition-colors"
          >
            Report something else
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-zinc-800 bg-zinc-900/40 p-6 md:p-8 space-y-7" data-testid="report-problem-form">
      <div>
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-red-400 mb-3">
          <AlertTriangle className="w-3 h-3" /> Report a problem
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white">
          Tell us what went wrong
        </h2>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
          The more specific you are — a timestamp, the video, a screenshot —
          the faster this gets fixed.
        </p>
      </div>

      {prefill?.cue_index && (
        <div className="border border-blue-500/30 bg-blue-500/5 p-4 space-y-2" data-testid="prefilled-cue-context">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-blue-300">
            <Clock className="w-3 h-3" /> Line #{String(prefill.cue_index).padStart(2, "0")} ·{" "}
            {fmtClock(prefill.cue_start)} → {fmtClock(prefill.cue_end)}
          </div>
          {prefill.source_text && (
            <div className="text-xs font-mono text-zinc-400">{prefill.source_text}</div>
          )}
          {prefill.generated_text && (
            <div className="text-sm text-white">{prefill.generated_text}</div>
          )}
          <div className="text-[10px] font-mono text-zinc-500">
            This exact line and its timestamp will be attached to the ticket.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
            Category
          </label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger
              data-testid="report-category-select"
              className="bg-zinc-950 border-zinc-800 rounded-none h-11 text-white"
            >
              <SelectValue placeholder="What kind of problem?" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-white rounded-none">
              {(meta?.categories || []).map((c) => (
                <SelectItem key={c.key} value={c.key} data-testid={`report-category-${c.key}`}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
            Severity
          </label>
          <Select value={severity} onValueChange={setSeverity}>
            <SelectTrigger
              data-testid="report-severity-select"
              className="bg-zinc-950 border-zinc-800 rounded-none h-11 text-white"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-white rounded-none">
              {(meta?.severities || []).map((s) => (
                <SelectItem key={s.key} value={s.key} data-testid={`report-severity-${s.key}`}>
                  <span className="flex items-center gap-2">
                    <span
                      className={`text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${SEVERITY_STYLE[s.key] || ""}`}
                    >
                      {s.label}
                    </span>
                    <span className="text-xs text-zinc-500">{SEVERITY_HINT[s.key]}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-[10px] font-mono text-zinc-600 mt-2 leading-relaxed">
            This is how it affects you — support sets its own internal priority.
          </div>
        </div>
      </div>

      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
          Related video {jobRequired
            ? <span className="text-red-400">· required for this category</span>
            : <span className="text-zinc-600">(optional)</span>}
        </label>
        <Select value={jobId} onValueChange={setJobId} disabled={!!prefill?.job_id}>
          <SelectTrigger
            data-testid="report-job-select"
            className="bg-zinc-950 border-zinc-800 rounded-none h-11 text-white"
          >
            <SelectValue placeholder="Pick one of your videos" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800 text-white rounded-none max-h-72">
            <SelectItem value="none">Not about a specific video</SelectItem>
            {jobs.map((j) => (
              <SelectItem key={j.job_id} value={j.job_id} data-testid={`report-job-${j.job_id}`}>
                <span className="flex items-center gap-2">
                  <Film className="w-3 h-3 text-zinc-600" />
                  <span className="max-w-[320px] truncate">{j.video_name}</span>
                  <span className="text-[10px] font-mono text-zinc-600">
                    {j.source_language} → {j.target_language} · {fmtDuration(j.duration_seconds)}
                  </span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {jobs.length === 0 && (
          <div className="text-[10px] font-mono text-zinc-600 mt-2">
            You have no processed videos yet — leave this as is.
          </div>
        )}
      </div>

      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
          Problem description
        </label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={5}
          maxLength={5000}
          data-testid="report-description-input"
          placeholder="What happened? Include a timestamp if it's about a specific moment."
          className="bg-zinc-950 border-zinc-800 rounded-none text-white placeholder:text-zinc-600 resize-none"
        />
        <div className="text-[10px] font-mono text-zinc-600 mt-1 text-right">
          {description.length}/5000
        </div>
      </div>

      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
          What were you expecting? <span className="text-zinc-600">(optional)</span>
        </label>
        <Textarea
          value={expected}
          onChange={(e) => setExpected(e.target.value)}
          rows={3}
          maxLength={2000}
          data-testid="report-expected-input"
          placeholder="What should have happened instead?"
          className="bg-zinc-950 border-zinc-800 rounded-none text-white placeholder:text-zinc-600 resize-none"
        />
      </div>

      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
          Attachment / screenshot <span className="text-zinc-600">(optional)</span>
        </label>
        <AttachmentPicker value={attachments} onChange={setAttachments} testId="report-attachment" />
      </div>

      <button
        type="submit"
        disabled={busy}
        data-testid="report-submit-btn"
        className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-50 px-6 py-3 transition-colors"
      >
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        Submit report
      </button>
    </form>
  );
};
